import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";
import SweetAlert from "react-bootstrap-sweetalert";
import Swal from "sweetalert2";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
  Typography,
  IconButton,
  Box,
  InputAdornment,
  Avatar,
  Card,
  CardContent,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Fade,
  Slide,
  Chip,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import PersonIcon from "@material-ui/icons/Person";
import BusinessIcon from "@material-ui/icons/Business";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import DescriptionIcon from "@material-ui/icons/Description";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import SearchIcon from "@material-ui/icons/Search";
import querystring from "querystring";
import StockCountUploadFileDialog from "./StockCountUploadFile";
import DialogProductSelectorForm from "../ProductSelector/DialogProductSelectorForm";
import { translator } from "../../utils/Translator";

import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";


// --- Styles (Updated to Orange Theme) ---
const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 20,
      boxShadow: "0 32px 64px rgba(233, 223, 223, 0.12)",
      overflow: "hidden",
      background: "#fafafa",
    },
  },
  dialogTitle: {
    background: "linear-gradient(135deg, #f97316 0%, #f97316 100%)",
    color: "white",
    padding: "24px 32px",
    position: "relative",
  },
  modernCard: {
    backgroundColor: "#ffffff",
    borderRadius: 16,
    border: "1px solid #e2e8f0",
    boxShadow: "0 4px 20px rgba(238, 229, 229, 0.08)",
    overflow: "hidden",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
    },
  },
  modernButton: {
    borderRadius: 12,
    textTransform: "none",
    fontWeight: 600,
    padding: "10px 24px",
    fontSize: "0.875rem",
    transition: "all 0.2s ease",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    },
  },
  primaryButton: {
    background: "linear-gradient(135deg, #fdba74 0%, #f97316 100%)",
    color: "white",
    "&:hover": {
      background: "linear-gradient(135deg, #fb923c 0%, #ea580c 100%)",
    },
  },
  secondaryButton: {
    backgroundColor: "#f8fafc",
    color: "#475569",
    border: "1px solid #e2e8f0",
    "&:hover": {
      backgroundColor: "#f1f5f9",
    },
  },
  accentButton: {
    background: "linear-gradient(135deg, #ff8c42 0%, #ff5c5c 100%)",
    color: "white",
    "&:hover": {
      background: "linear-gradient(135deg, #ff7b3a 0%, #fa4a4a 100%)",
    },
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: "#ffffff",
      border: "1px solid #e2e8f0",
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: "#cbd5e1",
        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
      },
      "&.Mui-focused": {
        borderColor: "#f97316",
        boxShadow: "0 4px 20px rgba(249, 115, 22, 0.15)",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
}));

// --- Confirm Save Dialog ---
const ConfirmSaveDialog = ({
  open,
  onClose,
  onConfirm,
  loading,
  title,
  message,
}) => {
  const classes = useStyles();
  const { translate } = useSelector(({ i18n }) => i18n);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 16,
          padding: "8px",
          background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
        },
      }}
    >
      <DialogTitle style={{ padding: "24px 32px 16px", textAlign: "center" }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <Box
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #f97316 0%, #f97316 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "2rem",
            }}
          >
            <DescriptionIcon />
          </Box>
          <Typography
            variant="h5"
            style={{ fontWeight: 600, color: "#1e293b" }}
          >
            {title || translator(translate.ISA_ConfirmEdit, "ยืนยันการแก้ไข")}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent style={{ padding: "0 32px 24px", textAlign: "center" }}>
        <Typography
          variant="body1"
          style={{ color: "#64748b", lineHeight: 1.6 }}
        >
          {message || translator(translate.ISA_ConfirmSaveEditMessage, "ต้องการบันทึกการแก้ไขนี้ใช่หรือไม่ ?")}
        </Typography>
      </DialogContent>
      <DialogActions
        style={{ padding: "0 32px 32px", justifyContent: "center", gap: 16 }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          size="large"
          style={{
            borderColor: "#e2e8f0",
            color: "#64748b",
            textTransform: "none",
            borderRadius: 12,
            padding: "12px 32px",
            fontWeight: 500,
            minWidth: 120,
          }}
        >
          {translator(translate.ISA_Cancel, "ยกเลิก")}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          size="large"
          style={{
            background: "linear-gradient(135deg, #f97316 0%, #f97316 100%)",
            color: "white",
            textTransform: "none",
            borderRadius: 12,
            padding: "12px 32px",
            fontWeight: 500,
            boxShadow: "0 8px 24px rgba(249, 115, 22, 0.4)",
            minWidth: 120,
          }}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} style={{ color: "white" }} />
          ) : (
            translator(translate.ISA_Confirm, "ยืนยัน")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Customer Select Dialog ---
const CustomerSelectDialog = ({ open, onClose, onSelect, customerList }) => {
  const classes = useStyles();
  const { translate } = useSelector(({ i18n }) => i18n);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const filteredList = customerList.filter(
    (cust) =>
      cust.customerCode.toLowerCase().includes(search.toLowerCase()) ||
      cust.customerName.toLowerCase().includes(search.toLowerCase())
  );

  const paginatedList = filteredList.slice(0, page * pageSize);

  useEffect(() => {
    setPage(1);
  }, [open, search]);

  const handleCustomerSelect = (customer) => {
    onSelect(customer);
    onClose();
    setSearch("");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className={classes.dialog}
      TransitionComponent={Slide}
      TransitionProps={{ direction: "up" }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography
          variant="h5"
          style={{
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <PersonIcon fontSize="large" />
          {translator(translate.ISA_SelectCustomer, "เลือกลูกค้า")}
        </Typography>
        <IconButton
          onClick={onClose}
          style={{
            position: "absolute",
            right: 16,
            top: 16,
            color: "white",
            backgroundColor: "rgba(255,255,255,0.2)",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.3)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Box
        style={{
          padding: "24px 32px 16px",
          background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder={translator(translate.ISA_SearchCustomer, "ค้นหาลูกค้า (รหัสหรือชื่อ)...")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={classes.textField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: "#64748b" }} />
              </InputAdornment>
            ),
            style: {
              borderRadius: 12,
              height: 48,
              fontSize: "1rem",
            },
          }}
        />
      </Box>
      <DialogContent dividers style={{ maxHeight: 500, padding: 0 }}>
        {paginatedList.length > 0 ? (
          <>
            {paginatedList.map((cust, index) => (
              <Fade in key={cust.customerCode} timeout={200 + index * 50}>
                <Box
                  onClick={() => handleCustomerSelect(cust)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "16px 32px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    borderBottom: "1px solid #f1f5f9",
                    background:
                      "linear-gradient(90deg, transparent 0%, rgba(249, 115, 22, 0.02) 100%)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#fff7ed";
                    e.currentTarget.style.transform = "translateX(8px)";
                    e.currentTarget.style.borderLeft = "4px solid #f97316";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.transform = "translateX(0)";
                    e.currentTarget.style.borderLeft = "none";
                  }}
                >
                  <Avatar
                    src={cust.img}
                    alt={cust.customerName}
                    style={{
                      width: 64,
                      height: 64,
                      marginRight: 20,
                      border: "3px solid #e2e8f0",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    }}
                  >
                    {cust.customerName?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box style={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      style={{
                        fontWeight: 600,
                        color: "#1e293b",
                        marginBottom: 4,
                        lineHeight: 1.3,
                      }}
                    >
                      {cust.customerName}
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{
                        color: "#64748b",
                        fontFamily: "monospace",
                        fontSize: "0.875rem",
                      }}
                    >
                      รหัส: {cust.customerCode}
                    </Typography>
                  </Box>
                </Box>
              </Fade>
            ))}
            {filteredList.length > paginatedList.length && (
              <Box style={{ padding: "20px", textAlign: "center" }}>
                <Button
                  onClick={() => setPage((prev) => prev + 1)}
                  variant="contained"
                  className={`${classes.modernButton} ${classes.primaryButton}`}
                >
                  โหลดเพิ่มเติม ({filteredList.length - paginatedList.length}{" "}
                  รายการ)
                </Button>
              </Box>
            )}
          </>
        ) : (
          <Box
            style={{
              textAlign: "center",
              padding: "60px 20px",
              color: "#64748b",
            }}
          >
            <PersonIcon
              style={{ fontSize: 80, color: "#cbd5e1", marginBottom: 16 }}
            />
            <Typography
              variant="h5"
              style={{ marginBottom: 8, color: "#475569", fontWeight: 500 }}
            >
              {translator(translate.ISA_CustomerNotFound, "ไม่พบลูกค้า")}
            </Typography>
            <Typography variant="body1" style={{ color: "#64748b" }}>
              {search
                ? `${translator(translate.ISA_NoCustomerMatchSearch, "ไม่พบลูกค้าที่ตรงกับ")} "${search}"`
                : translator(translate.ISA_NoCustomerData, "ไม่มีข้อมูลลูกค้า")}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// --- Main Dialog ---
const EditStockCountDialog = ({ open, onClose, initialData }) => {
  
  const classes = useStyles();
  const { authUser } = useSelector(({ auth }) => auth);
  const { languageSelected } = useSelector(({ settings }) => settings);
  const { translate } = useSelector(({ i18n }) => i18n);

  const [formState, setFormState] = useState({
    DocNumber: initialData?.docNo || "",
    DocDate: initialData?.docDate || moment().format("YYYY-MM-DD"),
    ActualCountDate:
      initialData?.realDate || moment().format("YYYY-MM-DD"),
    CountMethod: initialData?.countMethod || "onsite",
    Warehouse: initialData?.Warehouse || "",
    DocRemark: initialData?.DocRemark || "",
    items: [], // เริ่มต้นเป็น array ว่าง
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [customerList, setCustomerList] = useState([]);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [isCustomerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [isUploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [productItems, setProductItems] = useState([]);
  const [isProductSelectorOpen, setProductSelectorOpen] = useState(false);
  const [editingProductIndex, setEditingProductIndex] = useState(null);
  const [editingProductData, setEditingProductData] = useState(null);
  const [isStockCardOpen, setStockCardOpen] = useState(false);
  const [isSalesTargetOpen, setSalesTargetOpen] = useState(false);

  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (open && initialData) {
      
      // ตรวจสอบว่ามีข้อมูลสินค้าหรือไม่
      if (initialData.ProductCode) {
        
        // สร้าง item object จากข้อมูลที่ได้รับ
        const item = {
          id: Date.now(),
          productCode: initialData.ProductCode,
          productName: initialData.ProductName || translator(translate.ISA_ProductNameNotSpecified, "ไม่ระบุชื่อสินค้า"),
          lotNo: initialData.lotNo || "-",
          mfgDate: initialData.ItemMFGDate || null,
          expDate: initialData.ItemEXPDate || null,
          items: [
            {
              quantity: parseFloat(initialData.ProductQuan) || 0,
              unit: initialData.Unit || translator(translate.ISA_UnitPiece, "ชิ้น"),
            },
          ],
          remark: initialData.DocRemark || "",
        };
        
        
        setFormState((prev) => {
          const newState = {
            ...prev,
            DocNumber: initialData.docNo || prev.DocNumber,
            DocDate: initialData.docDate || prev.DocDate,
            ActualCountDate: initialData.realDate || prev.ActualCountDate,
            DocRemark: initialData.DocRemark || prev.DocRemark,
            items: [item],
          };
          return newState;
        });
      } else {
        setFormState((prev) => ({
          ...prev,
          DocNumber: initialData.docNo || prev.DocNumber,
          DocDate: initialData.docDate || prev.DocDate,
          ActualCountDate: initialData.realDate || prev.ActualCountDate,
          DocRemark: initialData.DocRemark || prev.DocRemark,
          items: [],
        }));
      }
    }
  }, [open, initialData]);


  // --- Helper Functions for Attachments ---
  const fetchFileStorage = async (token, relatedType, relatedId, company) => {
    try {
      const FILE_UPLOAD_URL =
        process.env.REACT_APP_FILE_UPLOAD_URL ||
        "https://services.ismartsales.com/file-manage/";
      const baseUrl = FILE_UPLOAD_URL.replace(/\/$/, "");
      const endpoint = `${baseUrl}/upload-multiple/${company ||
        "81"}/related/${relatedType}/${relatedId}`;

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        timeout: 15000,
        validateStatus: (status) => status < 500,
      });

      if (response.status === 200 || response.status === 201) {
        return (
          response.data?.data ||
          response.data?.FileStorage ||
          response.data?.files ||
          response.data ||
          []
        );
      }
      return [];
    } catch (error) {
      console.error(" fetchFileStorage error:", error);
      return [];
    }
  };

  const getFileUrl = (filePath, fileName, company, relatedType, relatedId) => {
    const FILE_UPLOAD_URL =
      process.env.REACT_APP_FILE_UPLOAD_URL ||
      "https://services.ismartsales.com/file-manage/";
    const baseUrl = FILE_UPLOAD_URL.replace(/\/$/, "");
    if (filePath && filePath.startsWith("https")) return filePath;
    if (company && relatedType && relatedId && fileName)
      return `${baseUrl}/upload-multiple/${company}/${relatedType}/${relatedId}/${fileName}`;
    if (filePath && fileName)
      return `${baseUrl}/upload-multiple/${filePath}/${fileName}`;
    if (fileName) return `${baseUrl}/upload-multiple/${fileName}`;
    return baseUrl;
  };

  const fetchAttachments = async (docNumber) => {
    if (!docNumber || !isMounted.current) return;
    setLoadingAttachments(true);
    try {
      const token = localStorage.getItem("x-access-token");
      const company = authUser?.company;
      const filesData = await fetchFileStorage(
        token,
        "stockcount",
        docNumber,
        company
      );

      if (isMounted.current) {
        const mappedFiles = filesData.map((file) => ({
          name: file.FileName,
          url: getFileUrl(
            file.FilePath,
            file.FileName,
            company,
            "stockcount",
            docNumber
          ),
        }));
        setAttachedFiles(mappedFiles);
      }
    } catch (err) {
      console.error("Error fetching attachments:", err);
      if (isMounted.current) setAttachedFiles([]);
    } finally {
      if (isMounted.current) setLoadingAttachments(false);
    }
  };

  // --- Fetch Customers ---
  const fetchCustomers = async () => {
    if (!authUser) return;
    if (isMounted.current) setLoadingCustomer(true);

    try {
      const company = authUser.company !== "#" ? authUser.company : "";
      const language = languageSelected.id;
      const params = {
        company,
        salesmanCode: authUser?.salesman || authUser?.username || "",
        language,
      };
      const queryString = querystring.stringify(params);
      const url = `${process.env.REACT_APP_API_URL}CustomerAccount/getCustomerList?${queryString}`;


      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("x-access-token")}`,
        },
      });

      const data = response?.data?.data || [];
      const mapped = data.map((cust, idx) => ({
        customerCode: cust.CustomerCode || `CUST${idx}`,
        customerName: cust.CustomerName || translator(translate.ISA_UnknownCustomer, "ไม่ทราบชื่อลูกค้า"),
        salesmanCode: cust.SalesmanCode || "",
        img:
          cust.CustomerImage ||
          "https://tse1.mm.bing.net/th/id/OIF.zbfew6OBJipfGY7rp8VYiw?r=0&rs=1&pid=ImgDetMain&o=7&rm=3",
      }));

      if (isMounted.current) setCustomerList(mapped);
    } catch (err) {
      console.error(" Fetch customers error:", err);
      if (isMounted.current) setCustomerList([]);
    } finally {
      if (isMounted.current) setLoadingCustomer(false);
    }
  };

  // --- Init Form ---
  useEffect(() => {
    if (open) {
      
      fetchCustomers();

      // ตั้งค่า customer ถ้ามี
      if (initialData?.customer) {
        const customerStr = initialData.customer.trim();
        const codeMatch = customerStr.match(/\(([^)]+)\)$/);
        if (codeMatch) {
          const customerData = {
            customerCode: codeMatch[1],
            customerName: customerStr
              .slice(0, customerStr.lastIndexOf("("))
              .trim(),
            img: initialData.customerImage || "https://via.placeholder.com/50",
          };
          setCustomer(customerData);
        }
      }

      // โหลดไฟล์แนบ
      if (initialData?.docNo) {
        fetchAttachments(initialData.docNo);
      }
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setError(null);
    if (!customer?.customerCode) {
      setError(translator(translate.ISA_PleaseSelectCustomer, "กรุณาเลือกลูกค้า"));
      return;
    }
    if (!formState.Warehouse) {
      setError(translator(translate.ISA_PleaseEnterWarehouse, "กรุณาระบุคลังสินค้า"));
      return;
    }
    setConfirmOpen(true);
  };

  const handleOpenUploadDialog = () => {
    if (formState.DocNumber) {
      setUploadDialogOpen(true);
    } else {
      alert(translator(translate.ISA_PleaseHaveDocNumberFirst, "กรุณามีเลขที่เอกสารก่อนแนบไฟล์"));
    }
  };

  const handleUploadSuccess = () => {
    setUploadDialogOpen(false);
    fetchAttachments(formState.DocNumber);
  };

  const handleDeleteFile = async (file, index) => {
    if (!window.confirm(translator(translate.ISA_ConfirmDeleteFile, `คุณต้องการลบไฟล์ "${file.name}" ใช่หรือไม่?`))) {
      return;
    }

    try {
      const token = localStorage.getItem("x-access-token");
      const FILE_UPLOAD_URL =
        process.env.REACT_APP_FILE_UPLOAD_URL ||
        "https://services.ismartsales.com/file-manage/";
      const baseUrl = FILE_UPLOAD_URL.replace(/\/$/, "");

      // ดึง fileName จาก fileUrl
      const fileUrl = file.url || "";
      const fileName = fileUrl.split(/[\/\\]/).pop();


      // ลบไฟล์โดยใช้ fileName
      const endpoint = `${baseUrl}/upload-multiple/${fileName}`;

      const response = await axios.delete(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        validateStatus: (status) => status < 500,
      });


      if (response.status === 200 || response.status === 204 || response.status === 201) {
        // ลบไฟล์ออกจาก state
        setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
        alert(translator(translate.ISA_DeleteFileSuccess, "ลบไฟล์เรียบร้อยแล้ว"));
        // Refresh attachments list
        fetchAttachments(formState.DocNumber);
      } else {
        console.warn(" Delete response:", response.status, response.data);
        alert(translator(translate.ISA_CannotDeleteFile, "ไม่สามารถลบไฟล์ได้ โปรดลองอีกครั้ง"));
      }
    } catch (error) {
      console.error(" Delete error:", error);
      alert(translator(translate.ISA_ErrorDeleteFile, "เกิดข้อผิดพลาดขณะลบไฟล์"));
    }
  };

  const handleConfirmSave = async () => {
    setLoading(true);
    setError(null);

    // แปลง Product items ให้เป็นรูปแบบที่ API ต้องการ
    const productItems = formState.items.map((item, index) => ({
      ItemNo: index + 1,
      ProductCode: item.productCode || item.product?.ProductCode,
      ProductName: item.productName || item.product?.ProductName,
      LotNo: item.lotNo || "",
      ItemMFGDate: item.mfgDate ? moment(item.mfgDate).format("YYYY-MM-DD") : null,
      ItemEXPDate: item.expDate ? moment(item.expDate).format("YYYY-MM-DD") : null,
      Unit: item.items && item.items.length > 0 ? item.items[0].unit : "",
      ProductQuan: item.items && item.items.length > 0 ? item.items[0].quantity : 0,
      ItemRemark: item.remark || "",
    }));

    const payload = {
      DocNumber: formState.DocNumber,
      DocDate: moment(formState.DocDate).format("YYYY-MM-DD"),
      DocRefDate: moment(formState.ActualCountDate).format("YYYY-MM-DD"),
      DocRemark: formState.DocRemark || "",
      Warehouse: formState.Warehouse,
      Status: "B",
      isActive: 1,
      Modifier: authUser?.displayName || authUser?.username || "SYSTEM",
      CustomerCode: customer?.customerCode || null,
      CustomerName: customer?.customerName || null,
      items: productItems, //  เพิ่มข้อมูล Product items
    };


    try {
      const result = await axios.post(
        `${process.env.REACT_APP_API_URL}CustomerAccount/updateStockTransaction/${formState.DocNumber}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("x-access-token")}`,
          },
        }
      );

      // ปิด ConfirmDialog ก่อน
      setConfirmOpen(false);
      setLoading(false);

      // ตรวจสอบว่า API ส่งกลับมา status 200-299 หรือไม่
      if (result.status >= 200 && result.status < 300) {
        // แสดง SweetAlert
        Swal.fire({
          title: translator(translate.ISA_Title_EditSuccess, "แก้ไขสำเร็จ"),
          text: translator(translate.ISA_FormSto, "ใบนับสต๊อก") + " " + translator(translate.ISA_DocNumber, "เลขที่เอกสาร") + " " + formState.DocNumber + " " + translator(translate.ISA_Msg_EditSuccess, "แก้ไขข้อมูลสำเร็จ"),
          icon: "success",
          timer: 2000, // Automatically close after 2 seconds
          showConfirmButton: false, // Hide the confirm button
        }).then(() => {
          onClose(true); // ส่ง true กลับไปยัง Menu_StockCountTable.js
        });
      } else {
        setError(result.data?.message || translator(translate.ISA_Title_Error, "เกิดข้อผิดพลาดในการบันทึกข้อมูล"));
      }
    } catch (error) {
      console.error("API error:", error.response?.data || error.message);
      setError(translator(translate.ISA_RegisterTrial_ConnectionError, "เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองอีกครั้ง"));
      // ลบ setConfirmOpen(false) เพื่อคง Dialog ไว้
    } finally {
      setLoading(false);
      setConfirmOpen(false);

      Swal.fire({
        title: translator(translate.ISA_Title_Error, "เกิดข้อผิดพลาด!"),
        text:
          error.response?.data?.message ||
          translator(translate.ISA_RegisterTrial_ConnectionError, "เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองอีกครั้ง"),
        icon: "error",
        confirmButtonText: translator(translate.ISA_Button_Ok, "ตกลง"),
        confirmButtonColor: "#ef4444",
      });
    }
  };

  const handleSuccessConfirm = () => {
    setShowSuccess(false);
    setConfirmOpen(false);
    // เพิ่มเงื่อนไขเพื่อไม่ให้ปิด Dialog หากต้องการ
    // onClose(true); // ลบหรือคอมเมนต์ถ้าไม่อยากให้ Dialog ปิด
  };
  const handleCancelConfirm = () => setConfirmOpen(false);

  const handleOpenCustomerDialog = () => setCustomerDialogOpen(true);
  const handleCloseCustomerDialog = () => setCustomerDialogOpen(false);
  const handleSelectCustomer = (cust) => {
    setCustomer(cust);
    setCustomerDialogOpen(false);
  };

  const handleOpenProductSelector = () => {
    
    if (!customer?.customerCode) {
      setError(translator(translate.ISA_PleaseSelectCustomerFirst, "กรุณาเลือกลูกค้าก่อนเพิ่มรายการสินค้า"));
      return;
    }
    
    
    // ปิด Dialog อื่นๆ ก่อนเปิด DialogProductSelectorForm
    if (isCustomerDialogOpen || isStockCardOpen || isSalesTargetOpen) {
      setCustomerDialogOpen(false);
      setStockCardOpen(false);
      setSalesTargetOpen(false);
    }
    
    setEditingProductIndex(null);
    setEditingProductData(null);
    
    setProductSelectorOpen(true);
  };

  const handleEditProduct = async (item, index) => {

  try {
    //  สร้าง product object จากข้อมูล item
    const productObject = item.product || {
      ProductCode: item.productCode,
      ProductName: item.productName,
      // สร้าง Unit1-4 จาก items
      ...(item.items && item.items.length > 0 && {
        Unit1: item.items[0]?.unit || translator(translate.ISA_UnitPiece, "ชิ้น"),
        Quantity1: 1,
        Price1: item.items[0]?.price || 0,
      }),
      ...(item.items && item.items.length > 1 && {
        Unit2: item.items[1]?.unit,
        Quantity2: item.items[1]?.quantity || 1,
        Price2: item.items[1]?.price || 0,
      }),
      ...(item.items && item.items.length > 2 && {
        Unit3: item.items[2]?.unit,
        Quantity3: item.items[2]?.quantity || 1,
        Price3: item.items[2]?.price || 0,
      }),
      ...(item.items && item.items.length > 3 && {
        Unit4: item.items[3]?.unit,
        Quantity4: item.items[3]?.quantity || 1,
        Price4: item.items[3]?.price || 0,
      }),
    };
    
    //  เตรียมข้อมูลสำหรับโหมด Edit ที่ส่งไปยัง DialogProductSelectorForm
    const editData = {
      id: item.id || Date.now(),
      product: productObject, //  ส่ง product object
      productCode: item.productCode || item.product?.ProductCode,
      productName: item.productName || item.product?.ProductName,
      lotNo: item.lotNo || "",
      mfgDate: item.mfgDate || null,
      expDate: item.expDate || null,
      items: item.items || [], //  ส่งข้อมูล items (จำนวนและหน่วย)
      remark: item.remark || "",
    };


    //  ตั้งค่า state สำหรับโหมด Edit
    setEditingProductIndex(index);
    setEditingProductData(editData);

    setProductSelectorOpen(true);
  } catch (error) {
    console.error(" Error in handleEditProduct:", error);
    Swal.fire({
      icon: "error",
      title: translator(translate.ISA_Error, "เกิดข้อผิดพลาด"),
      text: translator(translate.ISA_CannotLoadProductData, "ไม่สามารถโหลดข้อมูลสินค้าได้"),
    });
  }
};

  const handleDeleteProduct = (index) => {
    setProductItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleOpenStockCard = () => {
    if (!customer?.customerCode) {
      alert(translator(translate.ISA_PleaseSelectCustomerFirst, "กรุณาเลือกลูกค้าก่อน"));
      return;
    }
    setStockCardOpen(true);
  };

  const handleCloseStockCard = () => {
    setStockCardOpen(false);
  };

  const fetchData = async () => {
    try {
      // Add your API call or data fetching logic here
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          onClose(false);
        }}
        fullWidth
        maxWidth="bmd"
        className={classes.dialog}
        TransitionComponent={Slide}
        TransitionProps={{ direction: "up" }}
        disableBackdropClick // ป้องกันการปิดเมื่อคลิก backdrop
        disableEscapeKeyDown // ป้องกันการปิดเมื่อกด Esc
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h4"
              style={{
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 16,
                color: "white", // Updated color to white
              }}
            >
              <DescriptionIcon fontSize="large" />
              {translator(translate.ISA_EditStockCountDocument, "แก้ไขเอกสารนับสต๊อก")}
            </Typography>
            <IconButton
              onClick={() => onClose(false)}
              style={{
                color: "white",
                backgroundColor: "rgba(255,255,255,0.2)",
                padding: 12,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent style={{ padding: "32px", background: "#fafafa" }}>
          {/* Header Card */}
          <Card className={classes.modernCard} style={{ marginBottom: 24 }}>
            <CardContent style={{ padding: "32px" }}>
              <Typography
                variant="h6"
                style={{
                  fontWeight: 600,
                  marginBottom: 24,
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <DescriptionIcon />
                {translator(translate.ISA_DocumentHeaderTitle, "หัวเอกสาร")}
              </Typography>

              <Grid container spacing={4}>
                <Grid item xs={12} lg={8}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        style={{
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          marginBottom: 8,
                          color: "#374151",
                        }}
                      >
                        {translator(translate.ISA_DocNumber, "เลขที่เอกสาร")}
                      </Typography>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={formState.DocNumber || ""}
                        disabled
                        className={classes.textField}
                        InputProps={{
                          style: {
                            height: 48,
                            backgroundColor: "#f8fafc",
                            fontSize: "0.875rem",
                            fontFamily: "monospace",
                            fontWeight: 600,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        style={{
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          marginBottom: 8,
                          color: "#374151",
                        }}
                      >
                        {translator(translate.ISA_DocumentDate, "วันที่เอกสาร")}
                      </Typography>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        type="date"
                        name="DocDate"
                        value={formState.DocDate || ""}
                        onChange={handleChange}
                        className={classes.textField}
                        InputProps={{
                          style: { height: 48, fontSize: "0.875rem" },
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarTodayIcon
                                style={{ color: "#64748b", fontSize: "1.2rem" }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        style={{
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          marginBottom: 8,
                          color: "#374151",
                        }}
                      >
                        {translator(translate.ISA_StockCountDate, "วันที่นับจริง")}
                      </Typography>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        type="date"
                        name="ActualCountDate"
                        value={formState.ActualCountDate || ""}
                        onChange={handleChange}
                        className={classes.textField}
                        InputProps={{
                          style: { height: 48, fontSize: "0.875rem" },
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarTodayIcon
                                style={{ color: "#64748b", fontSize: "1.2rem" }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        style={{
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          marginBottom: 8,
                          color: "#374151",
                        }}
                      >
                        {translator(translate.ISA_CountMethod, "วิธีการนับ")}
                      </Typography>
                      <FormControl variant="outlined" size="small" fullWidth>
                        <Select
                          name="CountMethod"
                          value={formState.CountMethod || "onsite"}
                          onChange={handleChange}
                          className={classes.textField}
                          style={{ height: 48, fontSize: "0.875rem" }}
                        >
                          <MenuItem
                            value="onsite"
                            style={{ fontSize: "0.875rem" }}
                          >
                            {translator(translate.ISA_Onsite, "ผู้ใช้งาน Onsite")}
                          </MenuItem>
                          <MenuItem
                            value="remote"
                            style={{ fontSize: "0.875rem" }}
                          >
                            {translator(translate.ISA_Remote, "Remote")}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        style={{
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          marginBottom: 8,
                          color: "#374151",
                        }}
                      >
                        {translator(translate.ISA_Warehouse, "คลังสินค้า")}
                      </Typography>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        name="Warehouse"
                        placeholder={translator(translate.ISA_EnterWarehouse, "ระบุคลังสินค้า")}
                        value={formState.Warehouse || ""}
                        onChange={handleChange}
                        className={classes.textField}
                        InputProps={{
                          style: { height: 48, fontSize: "0.875rem" },
                          startAdornment: (
                            <InputAdornment position="start">
                              <BusinessIcon
                                style={{ color: "#64748b", fontSize: "1.2rem" }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography
                        style={{
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          marginBottom: 12,
                          color: "#374151",
                        }}
                      >
                        {translator(translate.ISA_UserInfo, "ข้อมูลผู้ใช้งาน")}
                      </Typography>
                      <Box
                        style={{
                          background:
                            "linear-gradient(135deg, #fdba74 0%, #f97316 100%)",
                          borderRadius: 16,
                          padding: "20px",
                          color: "white",
                        }}
                      >
                        <Box display="flex" alignItems="center">
                          <Avatar
                            src="https://tse2.mm.bing.net/th/id/OIP.R-pwwncmi43zAdvIPsYnBgHaEK?r=0&rs=1&pid=ImgDetMain&o=7&rm=3"
                            alt="user"
                            style={{
                              width: 60,
                              height: 60,
                              marginRight: 16,
                              border: "3px solid rgba(255,255,255,0.3)",
                              boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                            }}
                          />
                          <Box>
                            <Typography
                              style={{
                                fontSize: "1rem",
                                fontWeight: 600,
                                marginBottom: 4,
                              }}
                            >
                              {authUser?.SalesmanName ||
                                translator(translate.ISA_UnknownUser, "ไม่ทราบชื่อผู้ใช้")}
                            </Typography>
                            <Typography
                              style={{ fontSize: "0.75rem", opacity: 0.9 }}
                            >
                              {translator(translate.ISA_Code, "รหัส")}: {authUser?.salesman || authUser?.username}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                      >
                        <Typography
                          style={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            color: "#374151",
                          }}
                        >
                          {translator(translate.ISA_Customer_Info, "ข้อมูลลูกค้า")}
                        </Typography>
                        <Button
                          variant="contained"
                          onClick={handleOpenCustomerDialog}
                          startIcon={<PersonIcon />}
                          className={`${classes.modernButton} ${classes.primaryButton}`}
                          size="small"
                        >
                          {customer ? translator(translate.ISA_ChangeCustomer, "เปลี่ยนลูกค้า") : translator(translate.ISA_SelectCustomer, "เลือกลูกค้า")}
                        </Button>
                      </Box>
                      {customer ? (
                        <Card
                          style={{
                            border: "2px solid #f97316",
                            borderRadius: 16,
                            background:
                              "linear-gradient(135deg, #fff7ed 0%, #ffffff 100%)",
                          }}
                        >
                          <CardContent style={{ padding: "20px" }}>
                            <Box display="flex" alignItems="center">
                              <Avatar
                                src={customer.img}
                                alt={customer.customerName}
                                style={{
                                  width: 56,
                                  height: 56,
                                  marginRight: 16,
                                  border: "3px solid #f97316",
                                  boxShadow:
                                    "0 4px 12px rgba(249, 115, 22, 0.3)",
                                }}
                                onError={(e) => {
                                  e.target.src =
                                    "https://via.placeholder.com/56";
                                }}
                              />
                              <Box>
                                <Typography
                                  style={{
                                    fontWeight: 600,
                                    color: "#9a3412",
                                    fontSize: "1rem",
                                    marginBottom: 4,
                                  }}
                                >
                                  {customer.customerName}
                                </Typography>
                                <Typography
                                  style={{
                                    color: "#c2410c",
                                    fontSize: "0.875rem",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  รหัส: {customer.customerCode}
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      ) : (
                        <Card
                          style={{
                            border: "2px dashed #d1d5db",
                            borderRadius: 16,
                            background: "#f9fafb",
                          }}
                        >
                          <CardContent
                            style={{
                              padding: "40px 20px",
                              textAlign: "center",
                            }}
                          >
                            <PersonIcon
                              style={{
                                fontSize: 48,
                                color: "#9ca3af",
                                marginBottom: 8,
                              }}
                            />
                            <Typography
                              style={{ color: "#6b7280", fontWeight: 500 }}
                            >
                              กรุณาเลือกลูกค้า
                            </Typography>
                          </CardContent>
                        </Card>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card className={classes.modernCard} style={{ marginBottom: 24 }}>
            <CardContent style={{ padding: "32px" }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
              >
                <Typography
                  variant="h6"
                  style={{
                    fontWeight: 600,
                    color: "#1e293b",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <DescriptionIcon />
                  รายการสินค้านับสต๊อก
                </Typography>

                <Box display="flex" gap={2}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      if (!customer?.customerCode) {
                        setError(translator(translate.ISA_PleaseSelectCustomerFirst, "กรุณาเลือกลูกค้าก่อน"));
                        return;
                      }
                      setSalesTargetOpen(true);
                    }}
                    disabled={!customer?.customerCode}
                    className={`${classes.modernButton} ${classes.secondaryButton}`}
                  >
                    {translator(translate.ISA_StockCard, "Stock Card")}
                  </Button>
                  <Button
                    variant="contained"
                    className={`${classes.modernButton} ${classes.accentButton}`}
                    startIcon={<AddIcon />}
                    onClick={handleOpenProductSelector}
                    disabled={!customer?.customerCode}
                  >
                    เพิ่มรายการสินค้า
                  </Button>
                </Box>
              </Box>

              {formState.items?.length > 0 ? (
                <Box
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 12,
                    overflow: "hidden",
                    backgroundColor: "#ffffff",
                  }}
                >
                  {/* Table Header */}
                  <Box
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "60px 1fr 150px 120px 120px 120px 150px 80px",
                      backgroundColor: "#f8fafc",
                      borderBottom: "2px solid #e2e8f0",
                      padding: "16px",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      color: "#475569",
                    }}
                  >
                    <div style={{ textAlign: "center" }}>{translator(translate.ISA_Sequence, "ลำดับ")}</div>
                    <div>{translator(translate.ISA_ProductName, "ชื่อสินค้า")}</div>
                    <div style={{ textAlign: "center" }}>{translator(translate.ISA_ProductCode, "รหัสสินค้า")}</div>
                    <div style={{ textAlign: "center" }}>{translator(translate.ISA_LOT_NO, "Lot No.")}</div>
                    <div style={{ textAlign: "center" }}>{translator(translate.ISA_MfgDate, "วันที่ผลิต")}</div>
                    <div style={{ textAlign: "center" }}>{translator(translate.ISA_Lot, "วันหมดอายุ")}</div>
                    <div style={{ textAlign: "center" }}>{translator(translate.ISA_Qty, "จำนวน")}</div>
                    <div style={{ textAlign: "center" }}>{translator(translate.ISA_ManageTH, "จัดการ")}</div>
                  </Box>

                  {/* Table Body */}
                  {formState.items.map((item, index) => (
                    <Box
                      key={item.id || index}
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "60px 1fr 150px 120px 120px 120px 150px 80px",
                        borderBottom:
                          index < formState.items.length - 1
                            ? "1px solid #f1f5f9"
                            : "none",
                        padding: "16px",
                        alignItems: "center",
                        fontSize: "0.875rem",
                        transition: "background-color 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#f8fafc";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#ffffff";
                      }}
                    >
                      {/* ลำดับ */}
                      <div
                        style={{
                          textAlign: "center",
                          fontWeight: 600,
                          color: "#64748b",
                        }}
                      >
                        {index + 1}
                      </div>

                      {/* ชื่อสินค้า */}
                      <div>
                        <Typography
                          variant="body2"
                          style={{
                            fontWeight: 600,
                            color: "#1e293b",
                            marginBottom: 4,
                          }}
                        >
                          {item.productName || item.product?.ProductName}
                        </Typography>
                        {console.log("Product Name:", item.productName || item.product?.ProductName)}
                        {item.remark && (
                          <Typography
                            variant="caption"
                            style={{ color: "#64748b" }}
                          >
                            {item.remark}
                          </Typography>
                        )}
                      </div>

                      {/* รหัสสินค้า */}
                      <div style={{ textAlign: "center" }}>
                        <Chip
                          label={item.productCode || item.product?.ProductCode}
                          size="small"
                          style={{
                            backgroundColor: "#fef3c7",
                            color: "#92400e",
                            fontFamily: "monospace",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                          }}
                        />
                      </div>

                      {/* Lot No. */}
                      <div
                        style={{
                          textAlign: "center",
                          fontFamily: "monospace",
                          fontWeight: 600,
                          color: "#1e293b",
                        }}
                      >
                        {item.lotNo || "-"}
                      </div>

                      {/* วันที่ผลิต */}
                      <div style={{ textAlign: "center", color: "#475569" }}>
                        {item.mfgDate
                          ? moment(item.mfgDate).format("DD/MM/YYYY")
                          : "-"}
                      </div>

                      {/* วันหมดอายุ */}
                      <div style={{ textAlign: "center", color: "#475569" }}>
                        {item.expDate
                          ? moment(item.expDate).format("DD/MM/YYYY")
                          : "-"}
                      </div>

                      {/* จำนวน */}
                      <div style={{ textAlign: "center" }}>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          {item.items?.map((unitItem, idx) => (
                            <Box
                              key={idx}
                              style={{
                                padding: "4px 8px",
                                backgroundColor: "#f0fdf4",
                                borderRadius: 6,
                                border: "1px solid #bbf7d0",
                                display: "inline-block",
                              }}
                            >
                              <Typography
                                variant="caption"
                                style={{ fontWeight: 600, color: "#166534" }}
                              >
                                {unitItem.quantity} {unitItem.unit}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </div>

                      {/* จัดการ */}
                      <div style={{ textAlign: "center" }}>
                        <Box display="flex" gap={1} justifyContent="center">
                          <IconButton
                            size="small"
                            onClick={() => handleEditProduct(item, index)}
                            style={{
                              color: "#3b82f6",
                              padding: 8,
                              backgroundColor: "#eff6ff",
                              borderRadius: 8,
                            }}
                            title={translator(translate.ISA_Edit, "แก้ไข")}
                          >
                            <i
                              className="zmdi zmdi-edit"
                              style={{ fontSize: 18 }}
                            ></i>
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setFormState((prev) => ({
                                ...prev,
                                items: prev.items.filter((_, i) => i !== index),
                              }));
                            }}
                            style={{
                              color: "#ef4444",
                              padding: 8,
                              backgroundColor: "#fef2f2",
                              borderRadius: 8,
                            }}
                            title={translator(translate.ISA_Delete, "ลบ")}
                          >
                            <i
                              className="zmdi zmdi-delete"
                              style={{ fontSize: 18 }}
                            ></i>
                          </IconButton>
                        </Box>
                      </div>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box
                  style={{
                    background:
                      "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                    borderRadius: 12,
                    padding: "40px",
                    textAlign: "center",
                    border: "1px dashed #cbd5e1",
                  }}
                >
                  <DescriptionIcon
                    style={{ fontSize: 64, color: "#cbd5e1", marginBottom: 16 }}
                  />
                  <Typography
                    variant="h6"
                    style={{
                      color: "#475569",
                      marginBottom: 8,
                      fontWeight: 500,
                    }}
                  >
                    {translator(translate.ISA_NoProductYet, "ยังไม่มีรายการสินค้า")}
                  </Typography>
                  <Typography variant="body2" style={{ color: "#64748b" }}>
                    {translator(translate.ISA_ClickAddProductToStart, "กดปุ่ม \"เพิ่มรายการสินค้า\" เพื่อเริ่มต้นการนับสต๊อก")}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>



          <Card className={classes.modernCard}>
            <CardContent style={{ padding: "32px" }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
              >
                <Typography
                  variant="h6"
                  style={{
                    fontWeight: 600,
                    color: "#1e293b",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <AttachFileIcon />
                  ไฟล์แนบ
                </Typography>
                <Button
                  variant="outlined"
                  onClick={handleOpenUploadDialog}
                  startIcon={<AddIcon />}
                  className={`${classes.modernButton} ${classes.secondaryButton}`}
                >
                  เลือกไฟล์แนบ
                </Button>
              </Box>
              <Box display="flex" flexWrap="wrap" gap={2} mt={2}>
                {loadingAttachments ? (
                  <Box display="flex" alignItems="center" gap={2}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="textSecondary">
                      กำลังโหลดไฟล์...
                    </Typography>
                  </Box>
                ) : attachedFiles.length > 0 ? (
                  attachedFiles.map((file, index) => (
                    <Card
                      key={index}
                      style={{
                        width: 120,
                        height: 120,
                        borderRadius: 12,
                        overflow: "hidden",
                        border: "2px solid #e2e8f0",
                        position: "relative",
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.05)";
                        e.currentTarget.style.boxShadow =
                          "0 8px 32px rgba(0,0,0,0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "block",
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        <img
                          src={file.url}
                          alt={file.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src =
                              "https://via.placeholder.com/120?text=File";
                          }}
                        />
                      </a>
                      {/* ปุ่มลบไฟล์ */}
                      <IconButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteFile(file, index);
                        }}
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          backgroundColor: "rgba(239, 68, 68, 0.9)",
                          color: "white",
                          padding: 6,
                          zIndex: 10,
                          "&:hover": {
                            backgroundColor: "rgba(220, 38, 38, 1)",
                          },
                        }}
                        size="small"
                      >
                        <DeleteIcon style={{ fontSize: 16 }} />
                      </IconButton>
                    </Card>
                  ))
                ) : (
                  <Box
                    style={{
                      width: "100%",
                      padding: "40px",
                      textAlign: "center",
                      background:
                        "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",

                      borderRadius: 12,
                      border: "1px dashed #cbd5e1",
                    }}
                  >
                    <AttachFileIcon
                      style={{
                        fontSize: 48,
                        color: "#94a3b8",
                        marginBottom: 12,
                      }}
                    />
                    <Typography
                      variant="body1"
                      style={{ color: "#64748b", fontWeight: 500 }}
                    >
                      ยังไม่มีไฟล์แนบ
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          <Card className={classes.modernCard} style={{ marginTop: 24 }}>
            <CardContent style={{ padding: "32px" }}>
              <Typography
                variant="h6"
                style={{
                  fontWeight: 600,
                  marginBottom: 24,
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <DescriptionIcon />
                หมายเหตุ
              </Typography>
              <TextField
                multiline
                rows={7}
                variant="outlined"
                name="DocRemark"
                placeholder={translator(translate.ISA_DocumentRemarkTitle, "หมายเหตุท้ายบิล")}
                value={formState.DocRemark || ""}
                onChange={handleChange}
                fullWidth
                className={classes.textField}
                InputProps={{
                  style: {
                    borderRadius: 12,
                    fontSize: "0.875rem",
                    padding: "10px 16px",
                  },
                }}
              />
            </CardContent>
          </Card>

        </DialogContent>

        <DialogActions
          style={{
            padding: "24px 32px",
            background: "#ffffff",
            borderTop: "1px solid #e2e8f0",
            gap: 16,
          }}
        >
          {error && (
            <Box
              style={{
                marginRight: "auto",
                padding: "8px 16px",
                backgroundColor: "#fef2f2",
                borderRadius: 8,
                border: "1px solid #fecaca",
              }}
            >
              <Typography
                variant="body2"
                style={{ color: "#dc2626", fontWeight: 500 }}
              >
                {error}
              </Typography>
            </Box>
          )}
          <Box display="flex" gap={2} marginLeft="auto">
            <Button
              onClick={() => onClose(false)}
              variant="outlined"
              disabled={loading}
              className={`${classes.modernButton} ${classes.secondaryButton}`}
              size="large"
            >
              ยกเลิก
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={loading}
              className={`${classes.modernButton} ${classes.primaryButton}`}
              size="large"
            >
              {loading ? (
                <CircularProgress size={24} style={{ color: "white" }} />
              ) : (
                translator(translate.ISA_SaveEdit, "บันทึกการแก้ไข")
              )}
            </Button>
          </Box>
        </DialogActions>

        <ConfirmSaveDialog
          open={isConfirmOpen}
          onClose={handleCancelConfirm}
          onConfirm={handleConfirmSave}
          loading={loading}
        />
        <CustomerSelectDialog
          open={isCustomerDialogOpen}
          onClose={handleCloseCustomerDialog}
          onSelect={handleSelectCustomer}
          customerList={customerList}
        />
        <StockCountUploadFileDialog
          open={isUploadDialogOpen}
          onClose={() => setUploadDialogOpen(false)}
          onUploadSuccess={handleUploadSuccess}
          relatedId={formState.DocNumber}
        />
        {/* Product Selector Dialog */}
        {isProductSelectorOpen && (
          <DialogProductSelectorForm
  docType="DOC_STC"
  customer={customer}
  open={isProductSelectorOpen}
  action={editingProductIndex !== null ? "edit" : "add"} //  กำหนด action ตาม state
  editData={editingProductData} //  ส่งข้อมูลเดิมถ้าเป็นโหมด edit
  onCloseDialog={(productData) => {
    setProductSelectorOpen(false);

    if (productData) {

      if (editingProductIndex !== null) {
        //  โหมด Edit: อัปเดตข้อมูลเดิมใน formState.items
        setFormState((prev) => {
          const newItems = prev.items.map((item, idx) =>
            idx === editingProductIndex ? productData : item
          );
          return {
            ...prev,
            items: newItems,
          };
        });
      } else {
        //  โหมด Add: เพิ่มข้อมูลใหม่ใน formState.items
        setFormState((prev) => ({
          ...prev,
          items: [...(prev.items || []), productData],
        }));
      }
    }

    //  Reset editing state
    setEditingProductIndex(null);
    setEditingProductData(null);
  }}
/>
        )}

        {/* {translator(translate.ISA_StockCard, "Stock Card")} Dialog */}
        {isSalesTargetOpen && (
          <Dialog
            open={isSalesTargetOpen}
            onClose={() => setSalesTargetOpen(false)}
            fullWidth
            maxWidth="xl"
            PaperProps={{
              style: {
                borderRadius: 16,
                minHeight: "80vh",
                boxShadow: "0 24px 48px rgba(0,0,0,0.15)"
              }
            }}
          >
            <DialogTitle style={{
              padding: "24px",
              background: "linear-gradient(135deg, #f97316 0%, #f97316 100%)",
              color: "white"
            }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 12 }}>
                  ยอดระบาย
                </Typography>
                <IconButton
                  onClick={() => setSalesTargetOpen(false)}
                  size="small"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    color: "white"
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent style={{ padding: "24px" }}>
              <Typography variant="body1" style={{ color: "#64748b", textAlign: "center", padding: "40px" }}>
                ข้อมูล {translator(translate.ISA_StockCard, "Stock Card")} สำหรับลูกค้า {customer?.customerName}
              </Typography>
            </DialogContent>
          </Dialog>
        )}
      </Dialog>

      <SweetAlert
        show={showSuccess}
        success
        title={translator(translate.ISA_Msg_EditSuccess, "แก้ไขข้อมูลสำเร็จ")}
        onConfirm={handleSuccessConfirm}
        confirmBtnText={translator(translate.ISA_Button_Ok, "ตกลง")}
        timeout={2000}
      >
        เอกสารนับสต๊อกเลขที่ {formState.DocNumber} ถูกแก้ไขแล้ว
      </SweetAlert>
    </>
  );
};

export default EditStockCountDialog;
